import React, { useEffect, useState, useCallback, useRef } from 'react';
import './PhotoWall.css';
import FloatingPhoto from './FloatingPhoto';
import Balloon from './Balloon';
import CenterText from './CenterText';
import LogoEvento from './LogoEvento';
import PerformanceMonitor from './PerformanceMonitor';
import { API_ENDPOINTS, apiRequest } from '../config/api';
import { getImageUrl, getVideoUrl, processAssetParameters } from '../config/assets';
import { PositionManagerV2, ExclusionZone } from '../utils/PositionManagerV2';

interface Person {
  id: string;
  external_id: string;
  name: string;
  photo_url: string;
  speciality: string;
}

interface PhotoBubble {
  person: Person;
  isNew: boolean;
  speed: number;
  shouldEnter?: boolean;
  shouldExit?: boolean;
  managedPosition?: { x: number; y: number }; // Posição gerenciada pelo PositionManager
  cycleCount: number; // Contador de ciclos de fetch que a foto passou
  wasHighlighted: boolean; // Se a foto entrou como destaque (isNew=true)
  enteredAt: number; // Timestamp de quando a foto entrou na tela
}

interface BalloonInstance {
  id: string;
  position: { x: number; y: number };
  minSize: number;
  maxSize: number;
  maxOpacity: number;
  liveTime: number;
  imageSrc: string;
}

const PhotoWall: React.FC = () => {
  // PositionManager implementado para evitar sobreposições entre FloatingPhotos v2
  // Estados para gerenciar os FloatingPhotos
  const [photoBubbles, setPhotoBubbles] = useState<PhotoBubble[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  
  // Estado para gerenciar os Balloons
  const [balloons, setBalloons] = useState<BalloonInstance[]>([]);
  
  // Estado para armazenar os parâmetros de configuração
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [parametersLoaded, setParametersLoaded] = useState(false);
  
  // Estados para preloading de assets
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState('Inicializando...');
  
  // Ref para o vídeo precarregado
  const preloadedVideoRef = useRef<HTMLVideoElement | null>(null);
  
  // Estado para controlar visibilidade da página (Page Visibility API)
  const [isPageVisible, setIsPageVisible] = useState(true);
  
  // Ref para armazenar o interval dos balões para poder cancelar
  const balloonIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const balloonTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Ref para o gerenciador de posições V2
  const positionManagerRef = useRef<PositionManagerV2 | null>(null);
  
  // Ref para rastrear IDs ativos e evitar duplicatas (race conditions)
  const activePersonIdsRef = useRef<Set<string>>(new Set());
  
  // Função utilitária para deduplicar array de bubbles por person.id
  const deduplicateBubbles = (bubbles: PhotoBubble[]): PhotoBubble[] => {
    const seen = new Map<string, PhotoBubble>();
    // Iterar do fim para o início para manter a versão mais recente
    for (let i = bubbles.length - 1; i >= 0; i--) {
      const bubble = bubbles[i];
      if (!seen.has(bubble.person.id)) {
        seen.set(bubble.person.id, bubble);
      }
    }
    // Reconstruir array na ordem original (invertendo)
    return Array.from(seen.values()).reverse();
  };

  // Configurações de performance (podem ser movidas para parâmetros da API futuramente)
  const getPerformanceSettings = () => {
    // Detectar se é mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Detectar performance aproximada do device
    const hardwareConcurrency = navigator.hardwareConcurrency || 2;
    const deviceMemory = (navigator as any).deviceMemory || 4; // GB
    
    // Classificar performance
    const isLowPerformance = isMobile || hardwareConcurrency <= 2 || deviceMemory <= 2;
    const isMediumPerformance = hardwareConcurrency <= 4 || deviceMemory <= 4;
    
    if (isLowPerformance) {
      return {
        targetFPS: 30,
        enableThrottling: true,
        animationQuality: 'low' as const
      };
    } else if (isMediumPerformance) {
      return {
        targetFPS: 45,
        enableThrottling: true,
        animationQuality: 'medium' as const
      };
    } else {
      return {
        targetFPS: 60,
        enableThrottling: false,
        animationQuality: 'high' as const
      };
    }
  };

  const performanceSettings = getPerformanceSettings();
  
  // Ref para manter os parâmetros sempre atualizados no fetchPhotos
  const parametersRef = useRef<Record<string, string>>({});
  
  // Atualizar a ref sempre que parameters mudar
  useEffect(() => {
    parametersRef.current = parameters;
  }, [parameters]);

  // Função para calcular tamanho inicial da foto
  const calculateInitialPhotoSize = (isNew: boolean) => {
    const currentParams = parametersRef.current;
    if (isNew) {
      return Math.round((parseInt(currentParams.new_photo_size || '24') / 100) * window.innerHeight);
    } else {
      const sizePercentage = Math.random() * (parseInt(currentParams.max_photo_size || '14') - parseInt(currentParams.min_photo_size || '10')) + parseInt(currentParams.min_photo_size || '10');
      return Math.round((sizePercentage / 100) * window.innerHeight);
    }
  };

  // Função para gerar posições bem distribuídas na tela
  const generateWellDistributedPositions = (count: number) => {
    const positions: { x: number; y: number }[] = [];
    const margin = 0; // Margem das bordas
    const minDistance = 150; // Distância mínima entre balões

    for (let i = 0; i < count; i++) {
      let attempts = 0;
      let validPosition = false;
      let x:number, y:number;

      while (!validPosition && attempts < 50) {
        x = Math.round(margin + Math.random() * (window.innerWidth + 2 * margin));
        y = Math.round(margin + Math.random() * (window.innerHeight + 2 * margin));

        // Verificar se está longe o suficiente de outras posições
        validPosition = positions.every(pos => {
          const distance = Math.round(Math.sqrt((x! - pos.x) ** 2 + (y! - pos.y) ** 2));
          return distance >= minDistance;
        });

        attempts++;
      }

      if (validPosition) {
        positions.push({ x: x!, y: y! });
      }
    }

    return positions;
  };

  // Função para buscar parâmetros de configuração da API
  const fetchParameters = async () => {
    try {
      const response = await apiRequest(API_ENDPOINTS.GET_PARAMETERS);
      const data = await response.json();
      
      if (data.success) {
        // Processar parâmetros para converter caminhos de assets para URLs do S3
        const processedParams = processAssetParameters(data.parameters);
        setParameters(processedParams);
      } else {
        // Usar valores padrão em caso de erro
        setParameters({
          "show_time_logo": "15",
          "hidden_time_logo": "10",
          "balloon_create_interval": "4",
          "api_polling_interval": "10",
          "center_text": "Quem leva mais vida para os pacientes?",
          "center_text_color": "white",
          "center_text_font": "BASKVILL, serif",
          "number_of_photos": "50",
          "number_of_new_photos": "5",
          "min_photo_speed": "20",
          "max_photo_speed": "40",
          "min_photo_size": "10",
          "max_photo_size": "14",
          "new_photo_size": "24",
          "new_photo_highlight_time": "6",
          "time_to_fetch_photos": "10",
          "min_number_of_balloons": "5",
          "max_number_of_balloons": "10",
          "max_opacity_of_balloons": "10",
          "min_size_of_balloons": "10",
          "max_size_of_balloons": "35",
          "min_lifetime_of_balloons": "3",
          "max_lifetime_of_balloons": "6",
          "min_number_of_stars": "5",
          "max_number_of_stars": "10",
          "max_opacity_of_stars": "90",
          "min_size_of_stars": "10",
          "max_size_of_stars": "35",
          "min_lifetime_of_stars": "3",
          "max_lifetime_of_stars": "6",
          "center_text_widthPercentage": "0.44",
          "center_text_heightPercentage": "0.25",
          "center_text_typing_speed": "80",
          "center_text_display_duration": "5",
          "center_text_fadeout_duration": "1",
          "center_text_enable_typewriter": "true",
          "logo_evento": getImageUrl('logo.png'),
          "photowall_background_video": getVideoUrl('Tunnel.mp4'),
          "participant_name_font_size": "15",
          "exclusion_zone_top_left_width": "0",
          "exclusion_zone_top_left_height": "0"
        });
      }
    } catch (error) {
      // Usar valores padrão em caso de erro
        setParameters({
          "show_time_logo": "15",
          "hidden_time_logo": "10",
          "balloon_create_interval": "4",
          "api_polling_interval": "10",
          "center_text": "",
          "center_text_color": "white",
          "center_text_font": "BASKVILL, serif",
          "number_of_photos": "50",
          "number_of_new_photos": "5",
          "min_photo_speed": "20",
          "max_photo_speed": "30",
          "min_photo_size": "10",
          "max_photo_size": "14",
          "new_photo_size": "24",
          "new_photo_highlight_time": "6",
          "time_to_fetch_photos": "10",
          "min_number_of_balloons": "5",
          "max_number_of_balloons": "10",
          "max_opacity_of_balloons": "10",
          "min_size_of_balloons": "10",
          "max_size_of_balloons": "35",
          "min_lifetime_of_balloons": "3",
          "max_lifetime_of_balloons": "6",
          "min_number_of_stars": "5",
          "max_number_of_stars": "10",
          "max_opacity_of_stars": "90",
          "min_size_of_stars": "10",
          "max_size_of_stars": "35",
          "min_lifetime_of_stars": "3",
          "max_lifetime_of_stars": "6",
          "center_text_widthPercentage": "0.44",
          "center_text_heightPercentage": "0.25",
          "center_text_typing_speed": "80",
          "center_text_display_duration": "5",
          "center_text_fadeout_duration": "1",
          "center_text_enable_typewriter": "true",
          "logo_evento": getImageUrl('logo.png'),
          "photowall_background_video": getVideoUrl('Tunnel.mp4'),
          "participant_name_font_size": "15",
          "exclusion_zone_top_left_width": "0",
          "exclusion_zone_top_left_height": "0"
        });
    } finally {
      // Sempre marcar como carregado, mesmo em caso de erro
      setParametersLoaded(true);
    }
  };

  // Função para precarregar uma imagem
  const preloadImage = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => {
        console.warn(`⚠️ Falha ao carregar imagem: ${src}`);
        resolve(); // Não bloquear se uma imagem falhar
      };
      img.src = src;
    });
  };

  // Função para precarregar o vídeo de background
  const preloadVideo = (src: string): Promise<HTMLVideoElement> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      
      const timeout = setTimeout(() => {
        console.warn('⚠️ Timeout no carregamento do vídeo, continuando...');
        resolve(video);
      }, 30000); // 30 segundos de timeout
      
      video.oncanplaythrough = () => {
        clearTimeout(timeout);
        console.log('✅ Vídeo carregado e pronto para reproduzir');
        resolve(video);
      };
      
      video.onerror = () => {
        clearTimeout(timeout);
        console.warn(`⚠️ Falha ao carregar vídeo: ${src}`);
        resolve(video); // Não bloquear se o vídeo falhar
      };
      
      video.src = src;
      video.load();
    });
  };

  // Função para verificar se as fontes estão carregadas
  const waitForFonts = async (): Promise<void> => {
    try {
      // Aguardar as fontes personalizadas carregarem
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
        console.log('✅ Fontes carregadas');
      }
    } catch (error) {
      console.warn('⚠️ Erro ao aguardar fontes:', error);
    }
  };

  // Função principal para carregar todos os assets
  const loadAllAssets = async () => {
    try {
      setLoadingStatus('Carregando parâmetros...');
      setLoadingProgress(10);
      
      // Já temos os parâmetros carregados neste ponto
      
      // Lista de imagens essenciais para precarregar
      const essentialImages = [
        getImageUrl('balão_ache_01.png'),
        getImageUrl('star.png'),
        getImageUrl('Brilho2.png'),
        getImageUrl('Brilho3.png'),
      ];
      
      // Adicionar imagem do logo se configurada nos parâmetros
      if (parameters.logo_evento) {
        essentialImages.push(parameters.logo_evento);
      }
      
      setLoadingStatus('Carregando imagens...');
      setLoadingProgress(20);
      
      // Carregar todas as imagens em paralelo
      const imagePromises = essentialImages.map(src => preloadImage(src));
      let loadedImages = 0;
      
      await Promise.all(
        imagePromises.map(async (promise, index) => {
          await promise;
          loadedImages++;
          const imageProgress = 20 + (loadedImages / essentialImages.length) * 30;
          setLoadingProgress(Math.round(imageProgress));
        })
      );
      
      console.log(`✅ ${essentialImages.length} imagens carregadas`);
      
      // Carregar fontes
      setLoadingStatus('Carregando fontes...');
      setLoadingProgress(55);
      await waitForFonts();
      setLoadingProgress(60);
      
      // Carregar vídeo de background
      const videoUrl = parameters.photowall_background_video || getVideoUrl('Tunnel.mp4');
      setLoadingStatus('Carregando vídeo de fundo...');
      
      const video = await preloadVideo(videoUrl);
      preloadedVideoRef.current = video;
      
      setLoadingProgress(95);
      setLoadingStatus('Finalizando...');
      
      // Pequeno delay para garantir que tudo está pronto
      await new Promise(resolve => setTimeout(resolve, 200));
      
      setLoadingProgress(100);
      setLoadingStatus('Pronto!');
      
      // Marcar assets como carregados
      setAssetsLoaded(true);
      console.log('✅ Todos os assets foram carregados');
      
    } catch (error) {
      console.error('❌ Erro ao carregar assets:', error);
      // Mesmo com erro, permitir que a página carregue
      setAssetsLoaded(true);
    }
  };

  // useEffect para carregar assets após parâmetros serem carregados
  useEffect(() => {
    if (parametersLoaded && !assetsLoaded) {
      loadAllAssets();
    }
  }, [parametersLoaded]);

  // Função para criar balões aleatoriamente
  const createRandomBalloons = () => {
    // Verificar se a página está visível antes de criar balões
    if (!isPageVisible) {
      console.log('⏸️ Criação de balões pausada - página não visível');
      return;
    }

    // Verificar se já há muitos balões
    const maxBalloons = parseInt(parameters.max_number_of_balloons || '10');
    if (balloons.length >= maxBalloons * 1.5) {
      console.log(`⚠️ Muitos balões ativos (${balloons.length}/${maxBalloons * 1.5}), pulando criação`);
      return;
    }
    // Configuração de imagens com suas propriedades específicas
    const balloonConfig = [
      { 
        imageSrc: getImageUrl('balão_ache_01.png'), 
        maxOpacity: parseInt(parameters.max_opacity_of_balloons), 
        minCount: parseInt(parameters.min_number_of_balloons), 
        maxCount: parseInt(parameters.max_number_of_balloons),
        minSize: parseInt(parameters.min_size_of_balloons),
        maxSize: parseInt(parameters.max_size_of_balloons),
        minLifetime: parseInt(parameters.min_lifetime_of_balloons),
        maxLifetime: parseInt(parameters.max_lifetime_of_balloons)
      },
      { 
        imageSrc: getImageUrl('star.png'), 
        maxOpacity: parseInt(parameters.max_opacity_of_stars), 
        minCount: parseInt(parameters.min_number_of_stars), 
        maxCount: parseInt(parameters.max_number_of_stars),
        minSize: parseInt(parameters.min_size_of_stars),
        maxSize: parseInt(parameters.max_size_of_stars),
        minLifetime: parseInt(parameters.min_lifetime_of_stars),
        maxLifetime: parseInt(parameters.max_lifetime_of_stars)
      },
      // Adicione mais configurações aqui (imagem, opacidade, quantidade mínima, quantidade máxima)
    ];
    const newBalloons: BalloonInstance[] = [];
    
    // Para cada tipo de imagem, criar uma quantidade específica
    balloonConfig.forEach(config => {
      const count = Math.floor(Math.random() * (config.maxCount - config.minCount + 1)) + config.minCount; // minCount até maxCount
      const positions = generateWellDistributedPositions(count);
      
      const balloonsForThisImage = positions.map((position, index) => ({
        id: `balloon-${config.imageSrc.split('/').pop()?.split('.')[0]}-${Date.now()}-${index}`,
        position,
        minSize: config.minSize,
        maxSize: config.maxSize,
        maxOpacity: config.maxOpacity,
        liveTime: Math.random() * (config.maxLifetime - config.minLifetime) + config.minLifetime,
        imageSrc: config.imageSrc
      }));
      
      newBalloons.push(...balloonsForThisImage);
    });

    setBalloons(prev => [...prev, ...newBalloons]);
  };

  // Callback quando um balão é destruído
  const handleBalloonDestroy = (balloonId: string) => {
    setBalloons(prev => prev.filter(balloon => balloon.id !== balloonId));
  };

  // Aplicar estilos globais quando o componente é montado para garantir tela cheia sem scroll
  useEffect(() => {
    // Adicionar classe para remover scroll globalmente
    document.body.classList.add('photo-wall-no-scroll');
    document.documentElement.classList.add('photo-wall-no-scroll');

    // Cleanup quando o componente é desmontado
    return () => {
      document.body.classList.remove('photo-wall-no-scroll');
      document.documentElement.classList.remove('photo-wall-no-scroll');
    };
  }, []);

  // useEffect para monitorar visibilidade da página (Page Visibility API + eventos de foco)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsPageVisible(isVisible);
      
      if (isVisible) {
        console.log('🔍 Página voltou ao foco - limpando balões acumulados');
        // Limpar balões acumulados quando a página volta ao foco
        setBalloons([]);
      } else {
        console.log('👁️ Página saiu de foco - pausando criação de balões');
        // Cancelar timers ativos quando a página sai de foco
        if (balloonIntervalRef.current) {
          clearInterval(balloonIntervalRef.current);
          balloonIntervalRef.current = null;
        }
        if (balloonTimeoutRef.current) {
          clearTimeout(balloonTimeoutRef.current);
          balloonTimeoutRef.current = null;
        }
      }
    };

    // Eventos de backup para browsers mais antigos
    const handleFocus = () => {
      console.log('🔍 Janela ganhou foco - limpando balões acumulados');
      setIsPageVisible(true);
      setBalloons([]);
    };

    const handleBlur = () => {
      console.log('👁️ Janela perdeu foco - pausando criação de balões');
      setIsPageVisible(false);
    };

    // Adicionar event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    
    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // useEffect para limpeza automática de balões (evitar acúmulo excessivo)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setBalloons(prev => {
        // Se há muitos balões (mais que o máximo configurado), manter apenas os mais recentes
        const maxBalloons = parseInt(parameters.max_number_of_balloons || '10') * 2; // Permitir o dobro antes de limpar
        
        if (prev.length > maxBalloons) {
          console.log(`🧹 Limpeza automática: removendo ${prev.length - maxBalloons} balões antigos`);
          // Manter apenas os balões mais recentes (assumindo que são adicionados no final do array)
          return prev.slice(-maxBalloons);
        }
        
        return prev;
      });
    }, 10000); // Verificar a cada 10 segundos

    return () => clearInterval(cleanupInterval);
  }, [parameters.max_number_of_balloons]);

  // useEffect para buscar parâmetros de configuração ao carregar o componente
  useEffect(() => {
    fetchParameters();
  }, []);

  // Função para fazer pooling da API (usando useCallback para estabilidade)
  const fetchPhotos = useCallback(async () => {
    try {
      const response = await apiRequest(API_ENDPOINTS.REGISTRATION_PHOTOS);
      const data: { new: Person[], old: Person[] } = await response.json();
      const newPersons = data.new || [];
      const oldPersons = data.old || [];
      const allPersons = [...newPersons, ...oldPersons];

      // Usar parametersRef para obter valores sempre atualizados sem causar re-render
      const currentParams = parametersRef.current;

      if (isFirstLoad) {
        // Primeira carga: criar todos os FloatingPhotos com posições gerenciadas
        const initialBubbles: PhotoBubble[] = [];
        
        // Marcar todos os IDs na ref antes de criar os bubbles
        [...newPersons, ...oldPersons].forEach(person => {
          activePersonIdsRef.current.add(person.id);
        });
        
        // Contador para distribuição 50/50 entre metade superior e inferior
        let upperHalfCounter = 0;
        let lowerHalfCounter = 0;
        
        // Pessoas novas
        for (const person of newPersons) {
          const size = calculateInitialPhotoSize(true);
          
          // Alternar entre metade superior e inferior para distribuição equilibrada
          const preferUpperHalf = upperHalfCounter <= lowerHalfCounter;
          if (preferUpperHalf) {
            upperHalfCounter++;
          } else {
            lowerHalfCounter++;
          }
          
          // Fotos novas/destaque aparecem no terço central da tela
          const managedPosition = positionManagerRef.current?.registerPhoto(person.id, size, true, preferUpperHalf, true);
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`🆕 [PositionManager] Registrando nova foto: ${person.name} (${person.id}) - Posição: (${managedPosition?.x.toFixed(1)}, ${managedPosition?.y.toFixed(1)}) - Tamanho: ${size}px - Região: ${preferUpperHalf ? 'Superior' : 'Inferior'}`);
          }
          
          initialBubbles.push({
            person,
            isNew: true,
            speed: Math.random() * (parseInt(currentParams.max_photo_speed || '30') - parseInt(currentParams.min_photo_speed || '20')) + parseInt(currentParams.min_photo_speed || '20'),
            shouldEnter: true,
            managedPosition,
            cycleCount: 0,
            wasHighlighted: true, // Entrou como destaque
            enteredAt: Date.now() // Timestamp de entrada
          });
        }
        
        // Pessoas antigas - continuar a distribuição equilibrada
        for (const person of oldPersons) {
          const size = calculateInitialPhotoSize(false);
          
          // Continuar alternando entre metade superior e inferior
          const preferUpperHalf = upperHalfCounter <= lowerHalfCounter;
          if (preferUpperHalf) {
            upperHalfCounter++;
          } else {
            lowerHalfCounter++;
          }
          
          const managedPosition = positionManagerRef.current?.registerPhoto(person.id, size, false, preferUpperHalf);
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`👤 [PositionManager] Registrando foto existente: ${person.name} (${person.id}) - Posição: (${managedPosition?.x.toFixed(1)}, ${managedPosition?.y.toFixed(1)}) - Tamanho: ${size}px - Região: ${preferUpperHalf ? 'Superior' : 'Inferior'}`);
          }
          
          initialBubbles.push({
            person,
            isNew: false,
            speed: Math.random() * (parseInt(currentParams.max_photo_speed || '30') - parseInt(currentParams.min_photo_speed || '20')) + parseInt(currentParams.min_photo_speed || '20'),
            shouldEnter: true,
            managedPosition,
            cycleCount: 0,
            wasHighlighted: false, // Não entrou como destaque
            enteredAt: Date.now() // Timestamp de entrada
          });
        }

        // Deduplicar e atualizar ref de IDs ativos na primeira carga
        const deduplicated = deduplicateBubbles(initialBubbles);
        activePersonIdsRef.current = new Set(deduplicated.map(b => b.person.id));
        
        setPhotoBubbles(deduplicated);
        setIsFirstLoad(false);
      } else {
        // Atualizações subsequentes: gerenciar transições
        setPhotoBubbles(currentBubbles => {
          const currentPersonIds = currentBubbles.map(b => b.person.id);
          const newPersonIds = allPersons.map(p => p.id);

          // Incrementar contador de ciclos para todas as fotos existentes
          const bubblesWithIncrementedCycles = currentBubbles.map(bubble => ({
            ...bubble,
            cycleCount: bubble.cycleCount + 1,
            // Se a foto era destaque (isNew) e agora está na lista de "old", atualizar isNew
            isNew: bubble.isNew && newPersons.some(p => p.id === bubble.person.id)
          }));

          // Mínimo de ciclos que uma foto deve permanecer na tela
          const MIN_CYCLES = 3;
          
          // Tempo mínimo em milissegundos que uma foto deve permanecer na tela (15 segundos)
          const MIN_TIME_ON_SCREEN = 10000;
          const now = Date.now();

          // Identificar pessoas que devem sair (não estão na nova lista)
          // Proteger: fotos ainda em destaque (isNew), fotos que não completaram MIN_CYCLES,
          // e fotos que não passaram o tempo mínimo na tela
          const personsToExit = bubblesWithIncrementedCycles.filter(
            bubble => {
              const notInNewList = !newPersonIds.includes(bubble.person.id);
              const isStillHighlighted = bubble.isNew; // Ainda está em destaque
              const hasMinCycles = bubble.cycleCount >= MIN_CYCLES;
              const hasMinTime = (now - bubble.enteredAt) >= MIN_TIME_ON_SCREEN;
              
              // Só pode sair se: não está na lista E não está em destaque E já passou MIN_CYCLES E já passou tempo mínimo
              return notInNewList && !isStillHighlighted && hasMinCycles && hasMinTime;
            }
          );

          if (process.env.NODE_ENV === 'development') {
            const protectedByHighlight = bubblesWithIncrementedCycles.filter(
              b => !newPersonIds.includes(b.person.id) && b.isNew
            ).length;
            const protectedByCycles = bubblesWithIncrementedCycles.filter(
              b => !newPersonIds.includes(b.person.id) && !b.isNew && b.cycleCount < MIN_CYCLES
            ).length;
            const protectedByTime = bubblesWithIncrementedCycles.filter(
              b => !newPersonIds.includes(b.person.id) && !b.isNew && b.cycleCount >= MIN_CYCLES && (now - b.enteredAt) < MIN_TIME_ON_SCREEN
            ).length;
            if (protectedByHighlight > 0 || protectedByCycles > 0 || protectedByTime > 0) {
              console.log(`🛡️ Fotos protegidas: ${protectedByHighlight} em destaque, ${protectedByCycles} com menos de ${MIN_CYCLES} ciclos, ${protectedByTime} com menos de ${MIN_TIME_ON_SCREEN/1000}s na tela`);
            }
          }

          // Marcar no PositionManager as fotos que vão sair
          personsToExit.forEach(bubble => {
            positionManagerRef.current?.markPhotoAsExiting(bubble.person.id);
          });

          // Identificar pessoas que devem entrar (estão na nova lista mas não na atual)
          // Usar tanto currentPersonIds quanto activePersonIdsRef para evitar race conditions
          const personsToEnter = allPersons.filter(
            person => !currentPersonIds.includes(person.id) && !activePersonIdsRef.current.has(person.id)
          );

          // Marcar imediatamente os novos IDs na ref para evitar duplicatas em chamadas simultâneas
          personsToEnter.forEach(person => {
            activePersonIdsRef.current.add(person.id);
          });

          // Criar bubbles para as pessoas que devem entrar com posições gerenciadas
          const newBubbles: PhotoBubble[] = [];
          
          // Implementar distribuição 50/50 para novas entradas também
          let upperHalfCounter = 0;
          let lowerHalfCounter = 0;
          
          for (const person of personsToEnter) {
            const isNew = newPersons.some(p => p.id === person.id);
            const size = calculateInitialPhotoSize(isNew);
            
            // Alternar entre metade superior e inferior
            const preferUpperHalf = upperHalfCounter <= lowerHalfCounter;
            if (preferUpperHalf) {
              upperHalfCounter++;
            } else {
              lowerHalfCounter++;
            }
            
            // Fotos novas/destaque aparecem no terço central da tela
            const managedPosition = positionManagerRef.current?.registerPhoto(person.id, size, isNew, preferUpperHalf, isNew);
            
            newBubbles.push({
              person,
              isNew,
              speed: Math.random() * (parseInt(currentParams.max_photo_speed || '30') - parseInt(currentParams.min_photo_speed || '20')) + parseInt(currentParams.min_photo_speed || '20'),
              shouldEnter: true,
              managedPosition,
              cycleCount: 0,
              wasHighlighted: isNew, // Marca se entrou como destaque
              enteredAt: Date.now() // Timestamp de entrada
            });
          }

          // Atualizar estado: marcar para sair + adicionar novos
          // Deduplicar para evitar elementos duplicados (race conditions)
          let updatedBubbles = [
            // Bubbles existentes (com ciclos incrementados e algumas marcadas para sair)
            ...bubblesWithIncrementedCycles.map(bubble => ({
              ...bubble,
              shouldExit: personsToExit.some(p => p.person.id === bubble.person.id)
            })),
            // Novas bubbles marcadas para entrar
            ...newBubbles
          ];
          
          // Verificar número máximo de fotos e remover extras se necessário
          const maxPhotos = parseInt(currentParams.number_of_photos || '50');
          const activePhotos = updatedBubbles.filter(b => !b.shouldExit);
          
          if (activePhotos.length > maxPhotos) {
            const excessCount = activePhotos.length - maxPhotos;
            
            if (process.env.NODE_ENV === 'development') {
              console.log(`⚠️ Excesso de fotos: ${activePhotos.length}/${maxPhotos}. Removendo ${excessCount} fotos extras.`);
            }
            
            // Criar lista de candidatos para remoção (priorizando as que podem sair)
            // Prioridade 0: fotos que não estão em destaque E já passaram MIN_CYCLES E já passaram MIN_TIME_ON_SCREEN
            // Prioridade 1: fotos que não estão em destaque E já passaram MIN_CYCLES (mas não passaram tempo mínimo)
            // Prioridade 2: fotos que não estão em destaque (mas ainda não passaram MIN_CYCLES)
            // Nunca remover: fotos ainda em destaque ativo (isNew=true) ou que entraram há menos de 10 segundos
            
            const ABSOLUTE_MIN_TIME = 10000; // 10 segundos - proteção absoluta para fotos recém-entradas
            
            const removalCandidates = activePhotos
              .filter(b => {
                // Nunca remover fotos em destaque ativo
                if (b.isNew) return false;
                // Nunca remover fotos que entraram há menos de 10 segundos (proteção absoluta)
                if ((now - b.enteredAt) < ABSOLUTE_MIN_TIME) return false;
                return true;
              })
              .map(b => {
                // Calcular prioridade baseada em múltiplos fatores
                const hasMinCycles = b.cycleCount >= MIN_CYCLES;
                const hasMinTime = (now - b.enteredAt) >= MIN_TIME_ON_SCREEN;
                
                let priority: number;
                if (hasMinCycles && hasMinTime) {
                  priority = 0; // Mais prioritário para remoção
                } else if (hasMinCycles) {
                  priority = 1; // Passou ciclos mas não tempo
                } else if (hasMinTime) {
                  priority = 1; // Passou tempo mas não ciclos
                } else {
                  priority = 2; // Não passou nenhum critério
                }
                
                return { bubble: b, priority };
              })
              .sort((a, b) => {
                // Ordenar por prioridade, depois por cycleCount (mais antigos primeiro)
                if (a.priority !== b.priority) return a.priority - b.priority;
                return b.bubble.cycleCount - a.bubble.cycleCount; // Mais ciclos = mais antigo
              });
            
            // Selecionar aleatoriamente entre os candidatos de mesma prioridade
            const toRemove: string[] = [];
            let remaining = excessCount;
            
            // Agrupar por prioridade
            const byPriority: Map<number, typeof removalCandidates> = new Map();
            removalCandidates.forEach(c => {
              const list = byPriority.get(c.priority) || [];
              list.push(c);
              byPriority.set(c.priority, list);
            });
            
            // Remover da prioridade mais baixa primeiro
            for (const [priority, candidates] of Array.from(byPriority.entries()).sort((a, b) => a[0] - b[0])) {
              if (remaining <= 0) break;
              
              // Embaralhar candidatos de mesma prioridade para seleção aleatória
              const shuffled = [...candidates].sort(() => Math.random() - 0.5);
              
              for (const candidate of shuffled) {
                if (remaining <= 0) break;
                
                if (process.env.NODE_ENV === 'development') {
                  const timeOnScreen = ((now - candidate.bubble.enteredAt) / 1000).toFixed(1);
                  console.log(`🗑️ Marcando para remoção: ${candidate.bubble.person.name} (prioridade ${priority}, ${candidate.bubble.cycleCount} ciclos, ${timeOnScreen}s na tela)`);
                }
                
                toRemove.push(candidate.bubble.person.id);
                remaining--;
              }
            }
            
            if (process.env.NODE_ENV === 'development') {
              const protectedCount = activePhotos.filter(b => b.isNew || (now - b.enteredAt) < ABSOLUTE_MIN_TIME).length;
              console.log(`🗑️ Removendo ${toRemove.length} fotos extras para manter limite de ${maxPhotos} (${protectedCount} protegidas, ${remaining > 0 ? `${remaining} não puderam ser removidas` : 'ok'})`);
            }
            
            // Marcar fotos extras para saída
            toRemove.forEach(id => {
              positionManagerRef.current?.markPhotoAsExiting(id);
            });
            
            // Atualizar updatedBubbles para marcar as extras para saída
            updatedBubbles = updatedBubbles.map(bubble => ({
              ...bubble,
              shouldExit: bubble.shouldExit || toRemove.includes(bubble.person.id)
            }));
          }
          
          // Deduplicar e atualizar ref de IDs ativos
          const deduplicated = deduplicateBubbles(updatedBubbles);
          activePersonIdsRef.current = new Set(deduplicated.map(b => b.person.id));
          
          return deduplicated;
        });
      }
    } catch (error) {
      console.error('Erro ao buscar fotos:', error);
    }
  }, [isFirstLoad]); // Apenas isFirstLoad como dependência

  // useEffect para fazer a primeira busca de fotos (apenas uma vez)
  useEffect(() => {
    // Só iniciar após os parâmetros serem carregados
    if (!parametersLoaded) return;
    
    // Fazer primeira busca apenas uma vez
    fetchPhotos();
  }, [parametersLoaded, fetchPhotos]); // Incluir fetchPhotos nas dependências

  // useEffect separado para iniciar o pooling da API
  useEffect(() => {
    // Só iniciar após os parâmetros serem carregados
    if (!parametersLoaded) return;
    
    // Configurar pooling usando o parâmetro de intervalo (sem fazer primeira busca aqui)
    const intervalTime = parseInt(parameters.api_polling_interval || '15') * 1000;
    const interval = setInterval(fetchPhotos, intervalTime);
    
    // Cleanup
    return () => clearInterval(interval);
  }, [parametersLoaded, parameters.api_polling_interval, fetchPhotos]); // Incluir fetchPhotos nas dependências

  // useEffect para criar balões aleatoriamente
  useEffect(() => {
    // Só iniciar após os parâmetros serem carregados e a página estar visível
    if (!parametersLoaded || !isPageVisible) {
      // Limpar timers existentes se a página não estiver visível
      if (balloonIntervalRef.current) {
        clearInterval(balloonIntervalRef.current);
        balloonIntervalRef.current = null;
      }
      if (balloonTimeoutRef.current) {
        clearTimeout(balloonTimeoutRef.current);
        balloonTimeoutRef.current = null;
      }
      return;
    }
    
    // Criar primeiro conjunto de balões após 1 segundo (só se página visível)
    balloonTimeoutRef.current = setTimeout(() => {
      if (isPageVisible) {
        createRandomBalloons();
      }
    }, 1000);
    
    // Configurar criação periódica usando o parâmetro de intervalo
    const intervalTime = parseInt(parameters.balloon_create_interval) * 1000;
    balloonIntervalRef.current = setInterval(() => {
      if (isPageVisible) {
        createRandomBalloons();
      }
    }, intervalTime);
    
    // Cleanup
    return () => {
      if (balloonTimeoutRef.current) {
        clearTimeout(balloonTimeoutRef.current);
        balloonTimeoutRef.current = null;
      }
      if (balloonIntervalRef.current) {
        clearInterval(balloonIntervalRef.current);
        balloonIntervalRef.current = null;
      }
    };
  }, [parametersLoaded, parameters.balloon_create_interval, isPageVisible]); // Agora depende também da visibilidade

  // Callback quando um FloatingPhoto completa sua saída
  const handlePhotoExitComplete = (personId: string) => {
    // Remover do PositionManager
    positionManagerRef.current?.removePhoto(personId);
    
    // Remover da ref de IDs ativos
    activePersonIdsRef.current.delete(personId);
    
    // Remover do estado
    setPhotoBubbles(prev => prev.filter(bubble => bubble.person.id !== personId));
  };

  // Callback quando uma imagem de participante falha ao carregar
  const handleImageError = (personId: string) => {
    // Log sempre visível para debug de problemas de carregamento de imagem
    console.warn(`⚠️ [PhotoWall] Removendo participante ${personId} devido a erro no carregamento da foto`);
    
    // Encontrar o nome da pessoa para log mais informativo
    const bubble = photoBubbles.find(b => b.person.id === personId);
    if (bubble) {
      console.warn(`⚠️ [PhotoWall] Foto com erro: ${bubble.person.name} - URL: ${bubble.person.photo_url}`);
    }
    
    // Remover do PositionManager
    positionManagerRef.current?.removePhoto(personId);
    
    // Remover da ref de IDs ativos
    activePersonIdsRef.current.delete(personId);
    
    // Remover do estado (silenciosamente, sem animação de saída)
    setPhotoBubbles(prev => prev.filter(bubble => bubble.person.id !== personId));
  };

  // Callback para atualizar posição no PositionManager
  const handlePositionUpdate = (personId: string, position: { x: number; y: number }, velocity: { x: number; y: number }) => {
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.001) {
      console.log(`📍 [PositionManager] Atualizando posição de ${personId}: (${position.x.toFixed(1)}, ${position.y.toFixed(1)})`);
    }
    
    const result = positionManagerRef.current?.updatePhotoPosition(personId, position, velocity);
    return result;
  };

  // Callback para atualizar tamanho no PositionManager
  const handleSizeUpdate = (personId: string, newSize: number) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`📏 [PositionManager] Atualizando tamanho de ${personId}: ${newSize}px`);
    }
    positionManagerRef.current?.updatePhotoSize(personId, newSize);
  };

  // Função para calcular os limites do componente CenterText
  const calculateCenterTextBounds = () => {
    // Verificar se os parâmetros foram carregados
    if (!parametersLoaded) {
      // Retornar valores padrão enquanto carrega
      return {
        centerTextSize: { width: 0, height: 0, fontSize: 0, lineHeight: 0.8 },
        bounds: { left: 0, right: 0, top: 0, bottom: 0, centerX: 0, centerY: 0, width: 0, height: 0 }
      };
    }

    const heightPercentage = parseFloat(parameters.center_text_heightPercentage);
    const text = parameters.center_text || '';

    const centerTextSize = {
      width: 0, // Será calculada automaticamente baseada no texto
      height: Math.round(window.innerHeight * heightPercentage),
      fontSize: Math.round(window.innerHeight * 0.35 * 0.3), // 30% da altura total do componente
      lineHeight: 0.8 // Reduzir espaçamento entre linhas
    };

    // Ajustar fontSize baseado na altura
    centerTextSize.fontSize = centerTextSize.height * parseFloat(parameters.center_text_fontSizePercentage || '0.1');

    // Calcular largura automaticamente baseada no texto
    // Usar um elemento temporário invisível para medir o texto
    const tempElement = document.createElement('div');
    tempElement.style.position = 'absolute';
    tempElement.style.visibility = 'hidden';
    tempElement.style.whiteSpace = 'nowrap';
    tempElement.style.fontSize = `${centerTextSize.fontSize}px`;
    tempElement.style.fontFamily = parameters.center_text_font || 'Arial';
    tempElement.style.fontWeight = 'normal';
    tempElement.textContent = text;
    
    document.body.appendChild(tempElement);
    const textWidth = tempElement.offsetWidth;
    document.body.removeChild(tempElement);
    
    // Definir largura como a largura do texto mais uma pequena margem
    centerTextSize.width = textWidth + 40; // 20px margem de cada lado

    // Calcular posição central (padrão do CenterText)
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2 -15;

    // Calcular limites (bounds) do componente
    const bounds = {
      left: centerX - centerTextSize.width / 2,
      right: centerX + centerTextSize.width / 2,
      top: centerY - centerTextSize.height / 2 - 20,
      bottom: centerY + centerTextSize.height / 2 -20 ,
      centerX: centerX,
      centerY: centerY,
      width: centerTextSize.width,
      height: centerTextSize.height
    };

    return { centerTextSize, bounds };
  };

  // Calcular uma vez e usar em todo o componente
  const { centerTextSize, bounds: centerTextBounds } = calculateCenterTextBounds();

  // Calcular a zona de exclusão do canto superior esquerdo
  const calculateTopLeftExclusionZone = (): ExclusionZone | undefined => {
    const width = parseInt(parameters.exclusion_zone_top_left_width || '0');
    const height = parseInt(parameters.exclusion_zone_top_left_height || '0');
    
    if (width <= 0 || height <= 0) {
      return undefined;
    }
    
    return {
      left: 0,
      top: 0,
      width,
      height
    };
  };

  const topLeftExclusionZone = calculateTopLeftExclusionZone();

  // Inicializar PositionManager quando os parâmetros são carregados
  useEffect(() => {
    if (parametersLoaded && centerTextBounds) {
      positionManagerRef.current = new PositionManagerV2(centerTextBounds, topLeftExclusionZone);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🎯 [PositionManagerV2] Inicializado com centerTextBounds:`, {
          left: centerTextBounds.left.toFixed(1),
          right: centerTextBounds.right.toFixed(1),
          top: centerTextBounds.top.toFixed(1),
          bottom: centerTextBounds.bottom.toFixed(1),
          width: centerTextBounds.width.toFixed(1),
          height: centerTextBounds.height.toFixed(1)
        });
        
        if (topLeftExclusionZone) {
          console.log(`🚫 [PositionManagerV2] Zona de exclusão superior esquerda:`, {
            width: topLeftExclusionZone.width,
            height: topLeftExclusionZone.height
          });
        }
        
        // Log do grid info
        const gridInfo = positionManagerRef.current.getGridInfo();
        console.log(`🔧 [PositionManagerV2] Grid info:`, gridInfo);
      }
    }
  }, [parametersLoaded, centerTextBounds, topLeftExclusionZone]);

  // Cleanup do PositionManager quando o componente é desmontado
  useEffect(() => {
    return () => {
      // PositionManagerV2 não precisa de cleanup especial
      positionManagerRef.current = null;
    };
  }, []);

  // Mostrar loading enquanto os parâmetros e assets não são carregados
  if (!parametersLoaded || !assetsLoaded) {
    return (
      <div className="photo-wall-container" style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100vw',
        height: '100vh',
        backgroundColor: '#000',
        gap: '20px'
      }}>
        {/* Spinner animado */}
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: '#fff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        
        {/* Status do carregamento */}
        <div style={{ 
          color: 'white', 
          fontSize: '18px',
          fontFamily: 'Arial, sans-serif'
        }}>
          {loadingStatus}
        </div>
        
        {/* Barra de progresso */}
        <div style={{
          width: '300px',
          height: '6px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${loadingProgress}%`,
            height: '100%',
            backgroundColor: '#fff',
            borderRadius: '3px',
            transition: 'width 0.3s ease-out'
          }} />
        </div>
        
        {/* Percentual */}
        <div style={{ 
          color: 'rgba(255, 255, 255, 0.6)', 
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif'
        }}>
          {loadingProgress}%
        </div>
        
        {/* CSS para animação de spin */}
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div className="photo-wall-container">
      {/* Vídeo de fundo preenchendo toda a tela */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="photo-wall-video"
      >
        <source src={parameters.photowall_background_video} type="video/mp4" />
        Seu navegador não suporta vídeos HTML5.
      </video>

      {/* Conteúdo da página - FloatingPhotos e Balloons */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none'
      }}>
        {/* FloatingPhotos */}
        {photoBubbles.map((bubble) => (
          <FloatingPhoto
            key={bubble.person.id}
            person={bubble.person}
            speed={bubble.speed}
            isNew={bubble.isNew}
            shouldEnter={bubble.shouldEnter}
            shouldExit={bubble.shouldExit}
            centerTextBounds={centerTextBounds}
            minPhotoSize={parseInt(parameters.min_photo_size)}
            maxPhotoSize={parseInt(parameters.max_photo_size)}
            newPhotoSize={parseInt(parameters.new_photo_size)}
            newPhotoHighlightTime={parseInt(parameters.new_photo_highlight_time || '6')}
            participantNameFontSize={parseInt(parameters.participant_name_font_size || '15')}
            onExitComplete={handlePhotoExitComplete}
            onImageError={handleImageError}
            managedPosition={bubble.managedPosition}
            onPositionUpdate={handlePositionUpdate}
            targetFPS={performanceSettings.targetFPS}
            enableThrottling={performanceSettings.enableThrottling}
            animationQuality={performanceSettings.animationQuality}
            centerTextBehavior={parameters.photo_centertextbehavior as 'avoid' | 'fade' || 'avoid'}
          />
        ))}
        
        {/* Balloons */}
        {balloons.map((balloon) => (
          <Balloon
            key={balloon.id}
            minSize={balloon.minSize}
            maxSize={balloon.maxSize}
            maxOpacity={balloon.maxOpacity}
            liveTime={balloon.liveTime}
            position={balloon.position}
            imageSrc={balloon.imageSrc}
            onDestroy={() => handleBalloonDestroy(balloon.id)}
          />
        ))}
        
        {/* Texto Central */}
        <CenterText
          text={parameters.center_text}
          width={centerTextSize.width}
          height={centerTextSize.height}
          fontSize={centerTextSize.fontSize}
          fontFamily={parameters.center_text_font}
          color={parameters.center_text_color}
          backgroundColor="transparent" // Sem fundo
          textAlign="center"
          lineHeight={centerTextSize.lineHeight}
          zIndex={1002} // Acima dos balloons
          // Parâmetros de animação typewriter
          typingSpeed={parseInt(parameters.center_text_typing_speed || '80')}
          displayDuration={parseInt(parameters.center_text_display_duration || '5')}
          fadeOutDuration={parseFloat(parameters.center_text_fadeout_duration || '1')}
          enableTypewriter={parameters.center_text_enable_typewriter !== 'false'}
          // backgroundImage={getImageUrl('Brilho2.png')}
          backgroundImagePersistent={true}  // Imagem some junto com o texto
          backgroundImageOffsetY={0}       // Ajuste vertical
          backgroundImageOpacity={0.8}       // 80% de opacidade          
          // cornerImage={getImageUrl('Brilho3.png')}
          // cornerImage={getImageUrl('Brilho3.png')}
          cornerImageSize={350}
          cornerImageOffsetX={120}
          cornerImageOffsetY={10}
          cornerImageFadeInDuration={0.8}
/>
        
        {/* Logo Evento */}
        {/* <LogoEvento
          imageSrc={parameters.logo_evento}
          width={Math.round(window.innerHeight * 0.2 * 1.5)} // Proporcional à altura (assumindo logo 3:2)
          height={Math.round(window.innerHeight * 0.2)} // 20% da altura da área visível
          position={{
            x: window.innerWidth - Math.round(window.innerHeight * 0.2 * 1.5) - 30, // Canto inferior direito com margem
            y: window.innerHeight - Math.round(window.innerHeight * 0.2) + 5 // Canto inferior direito com margem
          }}
          showTime={parseInt(parameters.show_time_logo)}
          hiddenTime={parseInt(parameters.hidden_time_logo)}
        /> */}
      </div>

      {/* Debug info (remover em produção) */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          top: 20,
          left: 20,
          zIndex: 1000,
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '10px',
          borderRadius: '5px',
          fontSize: '14px',
          pointerEvents: 'none'
        }}>
          <div>Total FloatingPhotos: {photoBubbles.length}</div>
          <div>Novos: {photoBubbles.filter(b => b.isNew).length}</div>
          <div>Antigos: {photoBubbles.filter(b => !b.isNew).length}</div>
          <div>Entrando: {photoBubbles.filter(b => b.shouldEnter).length}</div>
          <div>Saindo: {photoBubbles.filter(b => b.shouldExit).length}</div>
          <div>---</div>
          <div>Balloons ativos: {balloons.length}</div>
          <div>---</div>
          <div>CenterText Bounds:</div>
          <div>Left: {Math.round(centerTextBounds.left)}, Top: {Math.round(centerTextBounds.top)}</div>
          <div>Right: {Math.round(centerTextBounds.right)}, Bottom: {Math.round(centerTextBounds.bottom)}</div>
          <div>Size: {centerTextBounds.width}x{centerTextBounds.height}</div>
        </div>
      )} */}
      
      {/* Visualização dos bounds do CenterText (desenvolvimento) */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div
          style={{
            position: 'fixed',
            left: centerTextBounds.left,
            top: centerTextBounds.top,
            width: centerTextBounds.width,
            height: centerTextBounds.height,
            border: '2px dashed red',
            backgroundColor: 'rgba(255, 0, 0, 0.1)',
            pointerEvents: 'none',
            zIndex: 1001
          }}
        />
      )} */}

      {/* Visualização da zona de exclusão superior esquerda (desenvolvimento) */}
      {process.env.NODE_ENV === 'development' && topLeftExclusionZone && (
        <div
          style={{
            position: 'fixed',
            left: topLeftExclusionZone.left,
            top: topLeftExclusionZone.top,
            width: topLeftExclusionZone.width,
            height: topLeftExclusionZone.height,
            border: '2px dashed yellow',
            backgroundColor: 'rgba(255, 255, 0, 0.1)',
            pointerEvents: 'none',
            zIndex: 1001
          }}
        />
      )}

      {/* Monitor de Performance (desenvolvimento) */}
      <PerformanceMonitor 
        show={process.env.NODE_ENV === 'development'} 
        position="top-left" 
      />
    </div>
  );
};

export default PhotoWall;
